/**
 * Modal grande del mapa de uniones: diagrama, colores y configuración de claves.
 */
import React, { useEffect } from 'react';
import { ink, studio } from '../studioUi';
import { sourceLabel } from './engineConfig';
import JoinBlendCanvas, { JoinInspector, TYPE_MARK, colorForSource } from './JoinBlendCanvas';

export default function JoinMapModal({
    open,
    onClose,
    dataSources = [],
    joins = [],
    schemasBySource = {},
    activeSourceId,
    loadingColumns,
    onSelectSource,
    onBlend,
    onPatchJoin,
    onTestJoin,
    onAddJoin,
    onRemoveJoin,
    onClearJoins,
}) {
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (event) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    const joinSources = dataSources.filter((item) => item.kind === 'join');

    return (
        <div style={ui.overlay} onMouseDown={onClose}>
            <div style={ui.sheet} onMouseDown={(event) => event.stopPropagation()}>
                <header style={ui.head}>
                    <div>
                        <p style={studio.kicker}>Uniones</p>
                        <h2 style={ui.title}>Mapa de fuentes</h2>
                        <p style={ui.sub}>
                            Cada hoja tiene un color. Las líneas usan el color de origen. Los campos con el mismo nombre aparecen como coincidencias.
                        </p>
                    </div>
                    <div style={ui.headActions}>
                        <button type="button" style={ui.ghost} onClick={onAddJoin}>+ Unión</button>
                        {joins.length > 0 && (
                            <button type="button" style={ui.danger} onClick={onClearJoins}>Limpiar uniones</button>
                        )}
                        <button type="button" style={ui.close} onClick={onClose}>Cerrar</button>
                    </div>
                </header>
                <div style={ui.body}>
                    <div style={ui.map}>
                        <JoinBlendCanvas
                            expanded
                            showInspector={false}
                            dataSources={dataSources}
                            joins={joins}
                            schemasBySource={schemasBySource}
                            activeSourceId={activeSourceId}
                            loadingColumns={loadingColumns}
                            onSelectSource={onSelectSource}
                            onBlend={onBlend}
                            onPatchJoin={onPatchJoin}
                            onTestJoin={onTestJoin}
                            onRemoveJoin={onRemoveJoin}
                        />
                    </div>
                    <aside style={ui.side}>
                        <span style={ui.sideKicker}>Uniones</span>
                        {joinSources.length === 0 && (
                            <p style={studio.hint}>Aún no hay uniones. Arrastra una hoja sobre otra en el mapa.</p>
                        )}
                        {joinSources.map((source) => {
                            const join = joins.find((item) => item.id === source.joinId);
                            const tone = colorForSource(source.id, dataSources, joins);
                            const active = source.id === activeSourceId;
                            const left = dataSources.find((item) => item.id === join?.leftDataSourceId);
                            const right = dataSources.find((item) => item.id === join?.rightDataSourceId);
                            const keys = (join?.keys || []).filter((key) => key.left && key.right);
                            return (
                                <div
                                    key={source.id}
                                    style={{
                                        ...ui.joinCard,
                                        backgroundColor: active ? tone.fill : ink.surface,
                                        borderColor: tone.stroke,
                                    }}
                                >
                                    <button
                                        type="button"
                                        onClick={() => onSelectSource(source.id)}
                                        style={ui.joinCardBtn}
                                    >
                                        <span style={{ ...ui.joinType, color: tone.text }}>
                                            {TYPE_MARK[join?.type] || 'Unión'}
                                        </span>
                                        <span style={ui.joinName}>{sourceLabel(source)}</span>
                                        <span style={ui.joinMeta}>
                                            {sourceLabel(left)} → {sourceLabel(right)}
                                        </span>
                                        <span style={ui.joinMeta}>
                                            {keys.length ? keys.map((key) => `${key.left}=${key.right}`).join(' · ') : 'Sin claves'}
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        style={ui.joinKill}
                                        title="Eliminar unión"
                                        onClick={() => onRemoveJoin(join?.id || source.joinId)}
                                    >
                                        ×
                                    </button>
                                </div>
                            );
                        })}
                        <JoinInspector
                            dataSources={dataSources}
                            joins={joins}
                            schemasBySource={schemasBySource}
                            activeSourceId={activeSourceId}
                            loadingColumns={loadingColumns}
                            onPatchJoin={onPatchJoin}
                            onTestJoin={onTestJoin}
                            onRemoveJoin={onRemoveJoin}
                        />
                    </aside>
                </div>
            </div>
        </div>
    );
}

const ui = {
    overlay: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(28, 29, 33, 0.5)',
        zIndex: 60,
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
        padding: 16,
    },
    sheet: {
        width: 'min(1280px, 100%)',
        height: 'min(92vh, 920px)',
        backgroundColor: ink.surface,
        borderRadius: 18,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 28px 80px rgba(28, 29, 33, 0.28)',
    },
    head: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 12,
        padding: '16px 18px 12px',
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: ink.line,
        flexShrink: 0,
    },
    title: {
        margin: '4px 0 0',
        fontSize: '1.2rem',
        color: ink.text,
    },
    sub: {
        margin: '6px 0 0',
        fontSize: '0.78rem',
        color: ink.muted,
        maxWidth: 640,
        lineHeight: 1.4,
    },
    headActions: { display: 'flex', gap: 8, flexShrink: 0 },
    ghost: {
        appearance: 'none',
        padding: '8px 14px',
        borderRadius: 999,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        backgroundColor: ink.surface,
        color: ink.accentText,
        cursor: 'pointer',
        fontWeight: 700,
        fontFamily: 'inherit',
    },
    danger: {
        appearance: 'none',
        padding: '8px 14px',
        borderRadius: 999,
        borderWidth: 0,
        borderStyle: 'solid',
        borderColor: 'transparent',
        backgroundColor: ink.dangerSoft,
        color: ink.danger,
        cursor: 'pointer',
        fontWeight: 700,
        fontFamily: 'inherit',
    },
    close: {
        appearance: 'none',
        padding: '8px 14px',
        borderRadius: 999,
        borderWidth: 0,
        borderStyle: 'solid',
        borderColor: 'transparent',
        backgroundColor: ink.accent,
        color: ink.surface,
        cursor: 'pointer',
        fontWeight: 700,
        fontFamily: 'inherit',
    },
    body: {
        flex: 1,
        minHeight: 0,
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 340px',
        gap: 0,
    },
    map: {
        minWidth: 0,
        minHeight: 0,
        padding: 14,
        display: 'flex',
        overflow: 'hidden',
        backgroundColor: ink.wash,
    },
    side: {
        borderLeftWidth: 1,
        borderLeftStyle: 'solid',
        borderLeftColor: ink.line,
        padding: 14,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        backgroundColor: ink.surface,
    },
    sideKicker: {
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: ink.faint,
    },
    joinCard: {
        display: 'flex',
        alignItems: 'stretch',
        gap: 6,
        padding: '6px 6px 6px 0',
        borderRadius: 12,
        borderWidth: 1,
        borderStyle: 'solid',
        backgroundColor: ink.surface,
    },
    joinCardBtn: {
        appearance: 'none',
        flex: 1,
        minWidth: 0,
        textAlign: 'left',
        padding: '6px 10px',
        borderWidth: 0,
        backgroundColor: 'transparent',
        cursor: 'pointer',
        fontFamily: 'inherit',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },
    joinKill: {
        appearance: 'none',
        width: 28,
        height: 28,
        margin: '6px 6px 0 0',
        borderWidth: 0,
        borderRadius: 999,
        backgroundColor: ink.dangerSoft,
        color: ink.danger,
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontWeight: 700,
        flexShrink: 0,
    },
    joinType: {
        fontSize: '0.62rem',
        fontWeight: 800,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
    },
    joinName: {
        fontSize: '0.85rem',
        fontWeight: 700,
        color: ink.text,
    },
    joinMeta: {
        fontSize: '0.7rem',
        color: ink.muted,
    },
};
